import { computed, effect, inject, Injectable, signal } from '@angular/core';
import {
  BudgetCreationInput,
  BudgetModificationInput,
  CreateBudgetGQL,
  DeleteBudgetGQL,
  GetBudgetByInvestisseurGQL,
  UpdateBudgetGQL,
} from '@lib/models/generated/graphql';
import { InvestisseurService } from '@lib/services/investisseur.service';
import { QueryManagerService } from 'finzzle-angular-utils';
import { BudgetModel } from './models/budget.model';
@Injectable({ providedIn: 'root' })
export class BudgetService {
  private readonly investisseurService = inject(InvestisseurService);
  private readonly queryManagerService = inject(QueryManagerService);
  private readonly createBudgetGQL = inject(CreateBudgetGQL);
  private readonly updateBudgetGQL = inject(UpdateBudgetGQL);
  private readonly deleteBudgetGQL = inject(DeleteBudgetGQL);
  private readonly getBudgetByInvestisseurGQL = inject(GetBudgetByInvestisseurGQL);
  private readonly _budgets = signal<BudgetModel[]>([]);
  public readonly budgets = this._budgets.asReadonly();
  public readonly loading = signal<boolean>(false);
  private readonly investisseurCode = computed(() => this.investisseurService.investisseurSig()?.code);
  private readonly reloadBudgetsSignal = signal(0);
  private activeFetchId = 0;
  private readonly loadBudgetsEffect = effect(() => {
    const code = this.investisseurCode();
    void this.reloadBudgetsSignal();
    if (!code) {
      this.loading.set(false);
      this._budgets.set([]);
      return;
    }
    const fetchId = ++this.activeFetchId;
    this.startBudgetsFetch(code, fetchId);
  });
  public readonly ficheInvestisseurIds = computed(() => {
    return [this.investisseurService.investisseurSig()?.id, this.investisseurService.investisseurConjointSig()?.id].filter(
      (id): id is number => id !== undefined
    );
  });
  public requestBudgetsReload(): void {
    this.reloadBudgetsSignal.update(token => token + 1);
  }
  private async startBudgetsFetch(code: string, fetchId: number) {
    this.loading.set(true);
    try {
      const budgets = await this.getAllBudgets(code);
      if (this.activeFetchId !== fetchId) {
        return;
      }
      if (this.investisseurCode() !== code) {
        return;
      }
      this._budgets.set(budgets);
    } finally {
      if (this.activeFetchId === fetchId && this.investisseurCode() === code) {
        this.loading.set(false);
      }
    }
  }
  async createBudget(budget: BudgetCreationInput): Promise<number | undefined> {
    const response = await this.queryManagerService.handleMutationErrorsAsync(
      this.createBudgetGQL.mutate({
        variables: { input: budget },
      }),
      true
    );
    const budgetId = response?.data?.createBudget[0].id;
    if (!budgetId) {
      throw response.error;
    }
    this.afterBudgetUpdate();
    return budgetId;
  }
  async updateBudget(budget: BudgetModificationInput): Promise<number | undefined> {
    const response = await this.queryManagerService.handleMutationErrorsAsync(
      this.updateBudgetGQL.mutate({
        variables: { input: budget },
      }),
      true
    );
    const budgetId = response?.data?.updateBudget[0].id;
    if (!budgetId) {
      throw response.error;
    }
    this.afterBudgetUpdate();
    return budgetId;
  }
  async deleteBudget(id: number): Promise<boolean | undefined> {
    const response = await this.queryManagerService.handleMutationErrorsAsync(
      this.deleteBudgetGQL.mutate({
        variables: { budgetId: id },
      })
    );
    const result = response?.data?.deleteBudget;
    if (!result) {
      throw response.error;
    }
    this.afterBudgetUpdate();
    return result;
  }
  private async afterBudgetUpdate(): Promise<void> {
    this.requestBudgetsReload();
    await this.investisseurService.refreshRecueilState();
  }
  private async getAllBudgets(investisseurCode: string): Promise<BudgetModel[]> {
    // Ici true pour catch l'erreur sur la récupération de la personnePhysique
    // Et gestion des autres erreurs renvoyé à la librairie pour afficher la snackbar
    const queryResult = await this.queryManagerService.handleQueryErrorsAsync(
      this.getBudgetByInvestisseurGQL.fetch({
        fetchPolicy: 'network-only',
        variables: {
          code: investisseurCode,
        },
      }),
      true
    );
    this.investisseurService.manageErrorInvestisseurOutOfWallet(
      this.queryManagerService.getGraphQLErrors(queryResult),
      'budgetInvestisseurs'
    );

    const investisseur = queryResult.data!.investisseur;
    if (!investisseur?.allBudgets) {
      throw queryResult.error;
    }
    const investisseurFoyerFiscalId = investisseur.foyerFiscalId;
    const invBudgets = investisseur.allBudgets;
    const conjointBudgets =
      investisseur!.personnePhysique[0]?.relationCouplePersonnePhysique?.autrePersonnePhysique?.investisseur[0]?.allBudgets?.filter(
        b => !(b.foyerFiscalId === investisseurFoyerFiscalId)
      ) ?? [];
    let allBudgets = [...invBudgets, ...conjointBudgets];
    const seen = new Map();
    allBudgets = allBudgets.filter(item => {
      if (item.budgetId > 0) {
        if (seen.has(item.budgetId)) {
          return false;
        }
        seen.set(item.budgetId, true);
      }
      return true;
    });
    return allBudgets.map(budget => {
      const typeBudget = queryResult.data!.typesBudgets.find(type => type.code === budget.typeBudgetCode)!;
      const result: BudgetModel = {
        budgetId: budget.budgetId,
        designation: budget.designation ?? undefined,
        patrimoineId: budget.patrimoineId,
        foyerFiscalId: budget.foyerFiscalId,
        montant: budget.montant,
        typeBudgetId: typeBudget.id,
        typeBudgetCode: typeBudget.code,
        isRevenu: typeBudget.isRevenu,
        categorie: typeBudget.categorie,
        investisseurIds: budget.budgetInvestisseurs.map(i => i.investisseurId),
        budgetInvestisseurs: budget.budgetInvestisseurs.map(i => ({
          investisseurId: i.investisseurId,
          investisseurName: i.investisseur?.personnePhysique?.[0]
            ? `${i.investisseur.personnePhysique[0].prenom} ${i.investisseur.personnePhysique[0].nom}`
            : null,
          pourcentageDetention: i.pourcentageDetention,
          mensualite: this.getMensualite(budget.montant, i.pourcentageDetention, budget.dateFin),
          isInvestisseurFiche: this.ficheInvestisseurIds()?.includes(i.investisseurId!),
          montantAnnuel: this.getMontantAnnuel(budget.montant, i.pourcentageDetention, budget.dureeRestante, budget.capitalRestantDu),
        })),
        commentaire: budget.commentaire ?? undefined,
        isGenerated: budget.isGenerated,
      };
      result.montantTotalCouple = Math.round(
        result.budgetInvestisseurs?.filter(i => i.isInvestisseurFiche).reduce((sum, current) => sum + (current.montantAnnuel || 0), 0) || 0
      );
      return result;
    });
  }
  private getMensualite(montant: number, pourcentageDetention: number, dateFin: Date | null): number {
    if (dateFin && new Date(dateFin) <= new Date()) {
      return 0;
    }
    return (montant * pourcentageDetention) / 100 / 12;
  }
  private getMontantAnnuel(
    montant: number,
    pourcentageDetention: number,
    dureeRestante: number | null | undefined,
    capitalRestantDu: number
  ): number {
    if (dureeRestante === 0) {
      return 0;
    }
    const taux = pourcentageDetention / 100;
    if (dureeRestante == null) {
      return montant * taux;
    }
    return dureeRestante >= 12 ? montant * taux : capitalRestantDu * taux;
  }
  getDuplicate(budget: BudgetModel): BudgetModel {
    const designation = budget.designation ? `${budget.designation} (Copie)` : undefined;
    return { ...budget, budgetId: undefined, designation: designation, patrimoineId: undefined };
  }
}
